import React, { useRef } from 'react';
import type { FC } from 'react';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrag, useDrop } from 'react-dnd';

import { DragItemTypes, TDragItem, TReviewSnippetSelectionCardProps } from './types';

const style = {
	cursor: 'move',
};

export const ReviewSnippetSelectionCard: FC<TReviewSnippetSelectionCardProps> = ({ 
	id, 
	text, 
	reviewContext,
	index, 
	moveCard, 
	setCard 
	}) => {
	const ref = useRef<HTMLDivElement>(null);
	const [{ handlerId }, drop] = useDrop<
		TDragItem,
		void,
		{ handlerId: Identifier | null }
	>({
		accept: [DragItemTypes.SnippetSource],
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item: TDragItem, monitor) {
			if (!ref.current || monitor.getItemType() !== DragItemTypes.SnippetSource) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}

			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();

			// Get vertical middle
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			// Time to actually perform the action
			moveCard(dragIndex, hoverIndex);

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: DragItemTypes.SnippetSource,
		item: () => {
			return { id, index };
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const clearCard = () => {
		setCard({
			snippetID: id,
			snippetText: '',
		});
	};

	const opacity = isDragging ? 0 : 1;
	drag(drop(ref));

	const previewBox = (
		<div>
			<blockquote>
				{`${text ? `"${text}"` : 'Drag snippet from review here'}`}
			</blockquote>
			<cite className="mt-2 text-brand_grey-medium text-sm not-italic">
				{reviewContext?.sourceReviewerDisplayName && `${reviewContext.sourceReviewerDisplayName}`} 
				{reviewContext?.sourceReviewConnectionName && ` (${reviewContext.sourceReviewConnectionName})`}
			</cite>
		</div>
	);

	return (
		<div ref={ref} style={{ ...style, opacity }} data-handler-id={handlerId}>
			<div className="flex items-center gap-4 border-2 border-dashed rounded-md p-3">
				{previewBox}
				<ul className="ms-auto text-sm text-end flex flex-col gap-2 min-w-28">
					<li className={`${reviewContext?.sourceReviewID ? 'visible' : 'invisible'}`}>
						<strong className="block">
							Review ID
						</strong>
						{reviewContext?.sourceReviewID ? reviewContext?.sourceReviewID : ''}
					</li>
					<li 
						className="text-brand_status-error hover:underline cursor-pointer"
						onClick={clearCard}
					>
						Remove
					</li>
				</ul>
			</div>
		</div>
	);
};
